// The Implementation Specialists on BossDrop's own admin page
// (pages/ClientAccounts.js). Eric adds a specialist, answers their registered
// companies, and records money: a customer's payment (which earns the share)
// and a payout (which is what they've been paid). What he records here is what
// the specialist sees on bossdrop.ai/specialists/dashboard. Backend:
// server/specialists/specialistProgram.js, behind requirePlatformAdmin.
//
// Money can't be edited, only corrected with a negative entry, so each entry
// asks first.
import React from "react";
import { Auth } from "../helpers/auth";
import { dialog } from "../lib/dialog";
import { toast } from "../lib/toast";
import FancyDate from "./FancyDate";
import FancySelect from "./FancySelect";
import "./SpecialistsAdmin.css";

export const DEFAULT_SHARE = "20";

const MESSAGES = {
  name_required: "Add their name.",
  email_invalid: "Check the email address.",
  code_invalid: "A code is lowercase letters, numbers and hyphens, up to 40 characters.",
  share_invalid: "The share is a percentage above 0, up to 100.",
  email_taken: "That email already belongs to a specialist.",
  code_taken: "That code is taken. Try another.",
  company_required: "Add the company's name.",
  contact_email_invalid: "Check the contact's email address.",
  already_registered: "Another specialist already holds that company.",
  not_active: "This specialist's agreement has ended, so they can't register new companies.",
  amount_invalid: "Enter a dollar amount other than zero.",
  date_invalid: "Pick a date.",
  no_payment_to_refund: "There's no payment on this company to refund yet.",
  specialist_blocked: "This specialist is blocked, so this payment earns nothing. Make them active first if that's wrong.",
  closed_before: "That company was closed on them before. Use Register for them only if you mean to reopen it.",
  introduction_closed: "This company is declined, lost or lapsed. Change its status first if it should earn.",
  outside_share_period: "That date is outside the 12 months of this company's share.",
  link_not_sent: "No link sent: they've had three in the last 10 minutes, or they're blocked.",
  forbidden: "Only the BossDrop account can do this.",
  has_money: "They still have money on record. Correct what's earned and paid to $0.00 first, or use End agreement or Block to keep the record.",
};

const INTRO_STATUSES = [
  { value: "registered", label: "Waiting for you" },
  { value: "confirmed", label: "Confirmed" },
  { value: "declined", label: "Declined" },
  { value: "demo", label: "Demo booked" },
  { value: "pilot", label: "In pilot" },
  { value: "customer", label: "Paying customer" },
  { value: "lost", label: "Not moving forward" },
  { value: "lapsed", label: "Lapsed" },
];

function messageFor(err) {
  const code = err?.data?.error;
  return MESSAGES[code] || (err?.status === 401 ? "Your session ended. Sign in again." : "That didn't save. Please try again.");
}

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
export const money = (cents) => USD.format(Number(cents || 0) / 100);

// Money reads green, and red when it's a refund or a correction.
export const moneyTone = (cents) => (Number(cents) > 0 ? "specAdmin__good" : Number(cents) < 0 ? "specAdmin__bad" : "");

/** Whole cents from what was typed: "4,000", "$4000.5", "-120.25". Null when it isn't an amount. */
export function toCents(value) {
  const clean = String(value == null ? "" : value).replace(/[$,\s]/g, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(clean)) return null;
  const negative = clean.startsWith("-");
  const [whole, part = ""] = clean.replace("-", "").split(".");
  const cents = Number(whole) * 100 + Number(part.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents) || cents === 0) return null;
  return negative ? -cents : cents;
}

/** A first go at a code from a name: "Jane O'Neil" becomes "jane-oneil". */
export function suggestCode(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/, "");
}

// The day here, not in UTC: from 6 pm Central, UTC is already tomorrow.
const today = () => new Date().toLocaleDateString("en-CA");
const shortDay = (value) => (value ? String(value).slice(0, 10) : "");

function AddSpecialist({ onAdded }) {
  const blank = { name: "", email: "", firm: "", code: "", sharePct: DEFAULT_SHARE, sendLogin: true };
  const [form, setForm] = React.useState(blank);
  const [codeTouched, setCodeTouched] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setError("");
    setForm((f) => ({
      ...f,
      [key]: value,
      ...(key === "name" && !codeTouched ? { code: suggestCode(value) } : {}),
    }));
    if (key === "code") setCodeTouched(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const out = await Auth.createSpecialist({ ...form, sharePct: Number(form.sharePct) });
      toast.success(
        out.loginSent
          ? `${out.specialist.name} is set up, and their login link is on its way.`
          : `${out.specialist.name} is set up. Send their login link when you're ready.`,
      );
      setForm(blank);
      setCodeTouched(false);
      onAdded();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="specAdmin__form" onSubmit={submit} noValidate>
      <div className="crFieldRow">
        <label className="crField">
          <span className="crFieldLabel">Name</span>
          <input className="inputFancy" type="text" value={form.name} onChange={set("name")} disabled={busy} autoComplete="off" />
        </label>
        <label className="crField">
          <span className="crFieldLabel">Login email</span>
          <input
            className="inputFancy"
            type="text"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            value={form.email}
            onChange={set("email")}
            disabled={busy}
            autoComplete="off"
          />
        </label>
      </div>
      <div className="crFieldRow">
        <label className="crField">
          <span className="crFieldLabel">Firm (optional)</span>
          <input className="inputFancy" type="text" value={form.firm} onChange={set("firm")} disabled={busy} autoComplete="off" />
        </label>
        <label className="crField">
          <span className="crFieldLabel">Link code</span>
          <input
            className="inputFancy"
            type="text"
            autoCapitalize="none"
            spellCheck={false}
            value={form.code}
            onChange={set("code")}
            disabled={busy}
            autoComplete="off"
          />
        </label>
        <label className="crField specAdmin__share">
          <span className="crFieldLabel">Share %</span>
          <input className="inputFancy" type="text" inputMode="decimal" value={form.sharePct} onChange={set("sharePct")} disabled={busy} />
        </label>
      </div>
      <label className="specAdmin__check">
        <input type="checkbox" checked={form.sendLogin} onChange={set("sendLogin")} disabled={busy} />
        Email their login link now
      </label>
      <div className="specAdmin__actions">
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "Adding..." : "Add specialist"}
        </button>
      </div>
      {error ? (
        <div className="specAdmin__error small" role="alert">
          {error}
        </div>
      ) : null}
    </form>
  );
}

// A customer's payment or a payout: an amount, a day, a note, and a question first.
function MoneyForm({ label, amountLabel, example, hint, button, question, withService = false, onSave, onReload }) {
  const [amount, setAmount] = React.useState("");
  const [day, setDay] = React.useState(today);
  const [service, setService] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    const cents = toCents(amount);
    if (cents == null) {
      setError(MESSAGES.amount_invalid);
      return;
    }
    const ok = await dialog.confirm(question(cents, day));
    if (!ok) return;
    setBusy(true);
    setError("");
    try {
      await onSave({ cents, day, service, note });
      setAmount("");
      setNote("");
      setService("");
    } catch (err) {
      // No answer at all, so it may have saved: the list says, and pressing again could double it.
      if (!err?.status) {
        setError("We didn't hear back, so this may have saved. Check the list before trying again.");
        onReload?.();
      } else {
        setError(messageFor(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="specAdmin__money" onSubmit={submit} noValidate aria-label={label}>
      <label className="crField">
        <span className="crFieldLabel">{amountLabel}</span>
        <input
          className="inputFancy"
          type="text"
          inputMode="decimal"
          placeholder={example}
          value={amount}
          disabled={busy}
          onChange={(e) => {
            setAmount(e.target.value);
            setError("");
          }}
        />
      </label>
      <label className="crField">
        <span className="crFieldLabel">{withService ? "Date paid" : "Date"}</span>
        <FancyDate value={day} disabled={busy} ariaLabel={withService ? "Date paid" : "Date"} onChange={setDay} />
      </label>
      {withService ? (
        <label className="crField">
          <span className="crFieldLabel">Service starts (if different)</span>
          <FancyDate
            value={service}
            disabled={busy}
            ariaLabel="Service starts (if different)"
            placeholder="Same as date paid"
            clearable
            onChange={setService}
          />
        </label>
      ) : null}
      <label className="crField specAdmin__note">
        <span className="crFieldLabel">Note (they see it)</span>
        <input className="inputFancy" type="text" maxLength={300} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
      </label>
      <button type="submit" className="btn primary" disabled={busy}>
        {busy ? "Saving..." : button}
      </button>
      <div className="muted small specAdmin__hint">{hint}</div>
      {error ? (
        <div className="specAdmin__error small" role="alert">
          {error}
        </div>
      ) : null}
    </form>
  );
}

// A date saved as soon as it's picked.
function DateField({ label, value, disabled, clearable, placeholder, onSave }) {
  return (
    <label className="crField specAdmin__date">
      <span className="crFieldLabel">{label}</span>
      <FancyDate
        value={value || ""}
        disabled={disabled}
        ariaLabel={label}
        clearable={clearable}
        placeholder={placeholder}
        onChange={onSave}
      />
    </label>
  );
}

function Introduction({ intro, specialist, onChanged }) {
  const [note, setNote] = React.useState(intro.note || "");
  const [busy, setBusy] = React.useState(false);
  const [paying, setPaying] = React.useState(false);

  // A list reload brings the saved note back in.
  React.useEffect(() => setNote(intro.note || ""), [intro.note]);

  const save = async (body, done) => {
    setBusy(true);
    try {
      await Auth.updateSpecialistIntroduction(intro.id, body);
      toast.success(done);
      onChanged();
    } catch (err) {
      toast.error(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  const held = intro.protectedUntil ? `Held until ${shortDay(intro.protectedUntil)}` : "";
  const share = intro.shareEndsOn ? `Share ends ${intro.shareEndsOn}` : "";
  return (
    <div className="specAdmin__intro">
      <div className="specAdmin__introTop">
        <div className="specAdmin__introWho">
          <strong>{intro.company}</strong>
          <div className="muted small">
            {[intro.contactName, intro.contactEmail].filter(Boolean).join(", ") || "No contact given"}
            {" · "}
            {intro.source === "link" ? "booked through their link" : intro.source === "eric" ? "added by you" : "registered on their dashboard"}
            {" · "}
            {shortDay(intro.registeredAt)}
          </div>
          {intro.confirmedBySilence ? <div className="muted small">Confirmed because 5 business days passed with no answer.</div> : null}
          {intro.needsAnswer && intro.status === "registered" ? (
            <div className="muted small">Closed on an earlier introduction, so it waits for your answer.</div>
          ) : null}
          {held || share ? <div className="muted small">{[held, share].filter(Boolean).join(" · ")}</div> : null}
        </div>
        <div className="crField specAdmin__status">
          <div className="crFieldLabel">Status</div>
          <FancySelect
            value={intro.status}
            options={INTRO_STATUSES}
            ariaLabel="Status"
            disabled={busy}
            usePortal
            onChange={(status) => {
              // Picking the one it already has isn't a change.
              if (status !== intro.status) save({ status }, `${intro.company} updated.`);
            }}
          />
        </div>
      </div>
      <div className="specAdmin__introRow">
        <label className="crField specAdmin__note">
          <span className="crFieldLabel">Note (they see it)</span>
          <input
            className="inputFancy"
            type="text"
            maxLength={500}
            value={note}
            disabled={busy}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => {
              if (note !== (intro.note || "")) save({ note }, "Note saved.");
            }}
          />
        </label>
        <button type="button" className={paying ? "btn" : "btn specAdmin__go"} onClick={() => setPaying((v) => !v)}>
          {paying ? "Close" : "Record customer payment"}
        </button>
      </div>
      <div className="specAdmin__introRow specAdmin__introDates">
        <DateField
          label="Extend the hold to"
          value={shortDay(intro.extendedUntil)}
          disabled={busy}
          clearable
          placeholder="No extension"
          onSave={(day) => save({ extendedUntil: day }, day ? `${intro.company} held until ${day}.` : "Extension removed.")}
        />
        {intro.firstPaidOn ? (
          <DateField
            label="The 12 months start on"
            value={intro.firstPaidOn}
            disabled={busy}
            onSave={(day) => save({ firstPaidOn: day }, `${intro.company}'s 12 months now start on ${day}.`)}
          />
        ) : null}
      </div>
      {paying ? (
        <MoneyForm
          label={`Record a customer payment for ${intro.company}`}
          amountLabel="Customer paid, in dollars (negative for a refund)"
          example="4000"
          hint="For money this customer already paid you. It earns their share. No money moves here. One entry per month of service. For a prepayment, add each month when it starts."
          button="Record payment"
          withService
          onReload={onChanged}
          question={(cents, day) => ({
            title: cents < 0 ? `Record a ${money(-cents)} refund from ${intro.company}?` : `Record ${money(cents)} from ${intro.company}?`,
            body: `Dated ${day}, at ${intro.sharePct ?? specialist.sharePct}% for ${specialist.name}. It can't be edited afterward, only corrected with another entry.`,
            confirmLabel: "Record it",
          })}
          onSave={async ({ cents, day, service, note: entryNote }) => {
            const out = await Auth.recordSpecialistPayment(intro.id, {
              receivedCents: cents,
              paidOn: day,
              ...(service ? { serviceOn: service } : {}),
              note: entryNote,
            });
            toast.success(`Recorded. ${specialist.name}'s share: ${money(out.entry.amountCents)}.`);
            setPaying(false);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function AddCompany({ specialist, onAdded }) {
  const [company, setCompany] = React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await Auth.addSpecialistIntroduction(specialist.id, { company, contactName });
      toast.success(`${company.trim()} registered to ${specialist.name}.`);
      setCompany("");
      setContactName("");
      onAdded();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="specAdmin__money" onSubmit={submit} noValidate aria-label={`Register a company for ${specialist.name}`}>
      <label className="crField">
        <span className="crFieldLabel">Company</span>
        <input className="inputFancy" type="text" value={company} disabled={busy} onChange={(e) => setCompany(e.target.value)} />
      </label>
      <label className="crField">
        <span className="crFieldLabel">Contact</span>
        <input className="inputFancy" type="text" value={contactName} disabled={busy} onChange={(e) => setContactName(e.target.value)} />
      </label>
      <button type="submit" className="btn primary" disabled={busy}>
        {busy ? "Adding..." : "Register for them"}
      </button>
      {error ? (
        <div className="specAdmin__error small" role="alert">
          {error}
        </div>
      ) : null}
    </form>
  );
}

function Specialist({ specialist, onChanged }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const { summary, introductions, ledger } = specialist;

  const sendLink = async () => {
    setBusy(true);
    try {
      await Auth.sendSpecialistLogin(specialist.id);
      toast.success(`Login link sent to ${specialist.email}.`);
      onChanged();
    } catch (err) {
      toast.error(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    const questions = {
      ended: {
        title: `End ${specialist.name}'s agreement?`,
        body: "They can still sign in and see their earnings, and confirmed companies keep earning. They can't register new ones.",
        confirmLabel: "End agreement",
      },
      blocked: {
        title: `Block ${specialist.name}?`,
        body: "They're signed out at once, can't sign in again, and earn nothing on payments recorded from now on. Companies they hold that aren't paying yet open up to others. Use this for fraud or a serious breach of the terms.",
        confirmLabel: "Block",
        danger: true,
      },
      active: { title: `Make ${specialist.name} active again?`, confirmLabel: "Make active" },
    };
    if (!(await dialog.confirm(questions[status]))) return;
    setBusy(true);
    try {
      await Auth.updateSpecialist(specialist.id, { status });
      toast.success(`${specialist.name} updated.`);
      onChanged();
    } catch (err) {
      toast.error(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    // Money that still stands stays on the record, so there's nothing to ask yet.
    if (summary.earnedCents || summary.paidCents) {
      toast.error(MESSAGES.has_money);
      return;
    }
    const ok = await dialog.confirm({
      title: `Delete ${specialist.name}?`,
      body: "This removes them, their login and the companies they registered, for good. It can't be undone.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await Auth.deleteSpecialist(specialist.id);
      toast.success(`${specialist.name} deleted.`);
      onChanged();
    } catch (err) {
      toast.error(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  const waiting = introductions.filter((i) => i.status === "registered").length;
  return (
    <div className="crCard specAdmin__person">
      <div className="specAdmin__personTop">
        <div>
          <strong>{specialist.name}</strong>
          {specialist.status !== "active" ? (
            <span className={`chip specAdmin__chip${specialist.status === "blocked" ? " bad" : ""}`}>{specialist.status}</span>
          ) : null}
          {waiting ? <span className="chip warn specAdmin__chip">{waiting} waiting for you</span> : null}
          <div className="muted small">
            {specialist.email}
            {specialist.firm ? ` · ${specialist.firm}` : ""} · code {specialist.code} · {specialist.sharePct}% ·{" "}
            {specialist.lastLoginAt ? `last signed in ${shortDay(specialist.lastLoginAt)}` : "hasn't signed in yet"}
          </div>
        </div>
        <div className="specAdmin__totals">
          <span>
            <span className="muted small">Earned</span> <span className={moneyTone(summary.earnedCents)}>{money(summary.earnedCents)}</span>
          </span>
          <span>
            <span className="muted small">Paid</span> <span className={moneyTone(summary.paidCents)}>{money(summary.paidCents)}</span>
          </span>
          <span>
            <span className="muted small">Owed</span>{" "}
            <span className={summary.owedCents > 0 ? "specAdmin__owed" : moneyTone(summary.owedCents)}>{money(summary.owedCents)}</span>
          </span>
        </div>
      </div>
      <div className="specAdmin__actions">
        <button type="button" className="btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Close" : `Open (${introductions.length} ${introductions.length === 1 ? "company" : "companies"})`}
        </button>
        <button type="button" className="btn" onClick={sendLink} disabled={busy || specialist.status === "blocked"}>
          Send login link
        </button>
      </div>

      {open ? (
        <div className="specAdmin__body">
          {introductions.length ? (
            introductions.map((intro) => <Introduction key={intro.id} intro={intro} specialist={specialist} onChanged={onChanged} />)
          ) : (
            <p className="muted small">No companies registered yet.</p>
          )}

          <h4 className="specAdmin__sub">Register a company for them</h4>
          <AddCompany specialist={specialist} onAdded={onChanged} />

          <h4 className="specAdmin__sub">Record a payout to them</h4>
          <MoneyForm
            label={`Record a payout to ${specialist.name}`}
            amountLabel="Paid to them, in dollars (negative to correct one)"
            example="800"
            hint="For money you already sent them. It lowers what you owe them. No money moves here."
            button="Record payout"
            onReload={onChanged}
            question={(cents, day) => ({
              title: cents < 0 ? `Take ${money(-cents)} back off ${specialist.name}'s payouts?` : `Record a ${money(cents)} payout to ${specialist.name}?`,
              body: `Dated ${day}. It can't be edited afterward, only corrected with another entry.`,
              confirmLabel: "Record it",
            })}
            onSave={async ({ cents, day, note }) => {
              await Auth.recordSpecialistPayout(specialist.id, { amountCents: cents, paidOn: day, note });
              toast.success(`Payout recorded for ${specialist.name}.`);
              onChanged();
            }}
          />

          {ledger.length ? (
            <>
              <h4 className="specAdmin__sub">Money so far</h4>
              <div className="specAdmin__tableWrap">
                <table className="specAdmin__table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>What</th>
                      <th className="num">Customer paid</th>
                      <th className="num">Their share</th>
                      <th className="num">Paid to them</th>
                      <th>Payable by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((row) => (
                      <tr key={row.id}>
                        <td>{row.occurredOn}</td>
                        <td>
                          {row.kind === "payout" ? "Payout" : row.company}
                          {row.note ? <div className="muted small">{row.note}</div> : null}
                        </td>
                        <td className={`num ${moneyTone(row.receivedCents)}`}>{row.receivedCents == null ? "" : money(row.receivedCents)}</td>
                        <td className={`num ${row.kind === "earned" ? moneyTone(row.amountCents) : ""}`}>{row.kind === "earned" ? money(row.amountCents) : ""}</td>
                        <td className={`num ${row.kind === "payout" ? moneyTone(row.amountCents) : ""}`}>{row.kind === "payout" ? money(row.amountCents) : ""}</td>
                        <td>{row.payableBy || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          <h4 className="specAdmin__sub">Their agreement</h4>
          <div className="specAdmin__actions">
            {specialist.status !== "active" ? (
              <button type="button" className="btn specAdmin__go" onClick={() => setStatus("active")} disabled={busy}>
                Make active
              </button>
            ) : null}
            {specialist.status !== "ended" ? (
              <button type="button" className="btn btnDangerGhost" onClick={() => setStatus("ended")} disabled={busy}>
                End agreement
              </button>
            ) : null}
            {specialist.status !== "blocked" ? (
              <button type="button" className="btn btnDangerGhost" onClick={() => setStatus("blocked")} disabled={busy}>
                Block
              </button>
            ) : null}
            <button type="button" className="btn btnDanger" onClick={remove} disabled={busy}>
              Delete specialist
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function SpecialistsAdmin() {
  const [specialists, setSpecialists] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const [adding, setAdding] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const out = await Auth.listSpecialists();
      setSpecialists(out.specialists || []);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="specAdmin">
      <div className="specAdmin__head">
        <h3>Implementation specialists</h3>
        <button type="button" className={adding ? "btn" : "btn specAdmin__go"} onClick={() => setAdding((v) => !v)} aria-expanded={adding}>
          {adding ? "Close" : "Add a specialist"}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 4 }}>
        What you record here is what they see at bossdrop.ai/specialists/dashboard. Only a customer's payment earns a share.
      </p>
      {adding ? (
        <div className="crCard">
          <AddSpecialist
            onAdded={() => {
              setAdding(false);
              load();
            }}
          />
        </div>
      ) : null}
      {failed ? (
        <p className="specAdmin__error small" role="alert">
          Couldn't load the specialists. Reload to try again.
        </p>
      ) : null}
      {specialists && !specialists.length ? <p className="muted">No specialists yet.</p> : null}
      {(specialists || []).map((specialist) => (
        <Specialist key={specialist.id} specialist={specialist} onChanged={load} />
      ))}
    </div>
  );
}
